import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { companyService, Company } from '../services/companyService';

interface CompanyContextType {
  currentCompany: Company | null;
  companies: Company[] | null;
  loading: boolean;
  setCurrentCompany: (company: Company) => void;
  switchCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const [currentCompany, setCurrentCompanyState] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCompanies = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const { data, error } = await companyService.getUserCompanies();
      
      if (error) {
        console.error('Error loading companies:', error);
        return;
      }

      if (data && data.length > 0) {
        setCompanies(data);
        
        // Load saved company from localStorage or use first company
        const savedCompanyId = localStorage.getItem('selectedCompanyId');
        const companyToSelect = savedCompanyId 
          ? data.find(c => c.id === savedCompanyId) || data[0]
          : data[0];
        
        setCurrentCompanyState(companyToSelect);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const setCurrentCompany = useCallback((company: Company): void => {
    setCurrentCompanyState(company);
    localStorage.setItem('selectedCompanyId', company.id);
  }, []);

  const switchCompany = useCallback(async (companyId: string): Promise<void> => {
    if (!companies) return;
    
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setCurrentCompany(company);
    }
  }, [companies, setCurrentCompany]);

  const refreshCompanies = useCallback(async (): Promise<void> => {
    await loadCompanies();
  }, [loadCompanies]);

  const value: CompanyContextType = useMemo(() => ({
    currentCompany,
    companies,
    loading,
    setCurrentCompany,
    switchCompany,
    refreshCompanies
  }), [currentCompany, companies, loading, setCurrentCompany, switchCompany, refreshCompanies]);

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};